import React, { useCallback, useEffect, useState } from 'react';
import Spinner from '../Spinner';
import UpgradeModelController from '../../merode/controllers/UpgradeModelController';
import type { IModelVersion as IControllerModelVersion } from '../../merode/controllers/Controller';
import type { IProject } from '../../stores/projects/project';

interface ProjectLoaderProps {
  /** The project whose active version content should be loaded. */
  project: IProject;
}

/**
 * Project Loader
 *
 * - show a spinner
 * - load the project's content on component mount
 * - show an error if that fails
 */
export default function ProjectLoader({ project }: ProjectLoaderProps) {
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setError(false);
    // ProjectLoader is only rendered (by Project/index) once `project.model`
    // and its `activeVersion` exist and that version's content is still
    // unavailable, so both are non-null here — assert to keep the legacy logic.
    const version = project.model!.activeVersion!;
    if (!version.contentAvailable) {
      await version.loadContent();
      // The store `IModelVersion` is the same loaded node the controller's
      // narrower `IModelVersion` describes; cast at the construction
      // boundary only (same pattern as the committed forms).
      const controller = new UpgradeModelController(
        version as unknown as IControllerModelVersion
      );
      await controller.upgradeModel();
    }
  }, [project]);

  useEffect(() => {
    let active = true;
    load().catch(() => {
      if (active) {
        setError(true);
      }
    });
    return () => {
      active = false;
    };
  }, [load]);

  const renderError = () => {
    return (
      <div className="loading-error flex h-full items-center justify-center text-center">
        We're sorry, we couldn't load your model.
        <button
          type="button"
          onClick={() => load().catch(() => setError(true))}
          className="ml-2.5 cursor-pointer rounded-md border border-grey bg-white px-2.5 py-1.5 text-black hover:bg-light focus-visible:ring-2 focus-visible:ring-primary/40"
        >
          Retry
        </button>
      </div>
    );
  };

  const renderLoading = () => {
    return <Spinner />;
  };

  return error ? renderError() : renderLoading();
}
